/** @file drawSet: corner meshes per sheet (and per effect blend); uploads a frame's list in painter order. */
import type { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial.js';
import type { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { sortImpostors } from '@zilo/pose3d-raster/draw/sortImpostors';
import type { ImpostorList } from '@zilo/pose3d-render-core/contract';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import { cornerMesh, type CornerMesh } from '@zilo/pose3d-runtime-babylon/draw/cornerMesh';
import type { QuadFrame } from '@zilo/pose3d-runtime-babylon/draw/quadFrame';
import { quadMaterial } from '@zilo/pose3d-runtime-babylon/draw/quadMaterial';
import { sheetTexture } from '@zilo/pose3d-runtime-babylon/draw/sheetTexture';
import { effectMaterial } from '@zilo/pose3d-runtime-babylon/effect/effectMaterial';

/** The meshes of one puppet instance's impostors and effects. */
export type DrawSet = {
  upload(list: ImpostorList, count: number, frame: QuadFrame): void;
  setVisible(on: boolean): void;
  meshes(): readonly Mesh[];
  dispose(): void;
};

/** One mesh: which sheet and which blend (`null` = opaque impostors) it draws. */
type Part = { sheet: number; blend: 'alpha' | 'add' | null; mesh: CornerMesh; material: ShaderMaterial; size: [number, number] };

/** The blend of list entry `i`: its drawable's when it is an effect, else null. */
const blendOf = (puppet: Puppet, list: ImpostorList, i: number): Part['blend'] => {
  const d = puppet.drawables[list[i * L.stride + L.drawable] ?? -1];
  return d?.kind === 'effect' ? d.blend : null;
};

/**
 * Per sheet (in `Object.keys(puppet.sheets)` order, the list's sheet index) a `sheetTexture` with the profile's
 * sampling, a `quadMaterial` + `cornerMesh` for impostors and an `effectMaterial` + `cornerMesh` per blend its effect
 * drawables use. `upload` sorts once with raster's `sortImpostors` (far first, ties in list order) and gives each mesh
 * its entries in that order, so the depth test (LEQUAL) resolves ties as raster's painter does; ties across meshes
 * follow mesh order (opaque before blended).
 */
export function drawSet(scene: Scene, puppet: Puppet, sheets: readonly Image[], profile: Profile, name: string): DrawSet {
  const textures: RawTexture[] = [];
  const parts: Part[] = Object.keys(puppet.sheets).flatMap((sheet, s) => {
    const image = sheets[s];
    if (image === undefined) throw new Error(`drawSet: no image for sheet ${sheet}`);
    const texture = sheetTexture(scene, image, profile.sampling, `${name}/${sheet}`);
    textures.push(texture);
    const size: [number, number] = [image.width, image.height];
    const blends = [...new Set(puppet.drawables.flatMap((d) => (d.kind === 'effect' && d.frames.sheet === sheet ? [d.blend] : [])))];
    const opaque = quadMaterial(scene, texture, profile.sampling, `${name}/${sheet}`);
    return [
      { sheet: s, blend: null, material: opaque, mesh: cornerMesh(scene, opaque, `${name}/${sheet}`, 8), size },
      ...blends.map((blend): Part => {
        const material = effectMaterial(scene, texture, blend, profile.sampling, `${name}/${sheet}/${blend}`);
        return { sheet: s, blend, material, mesh: cornerMesh(scene, material, `${name}/${sheet}/${blend}`, 4), size };
      }),
    ];
  });
  return {
    upload: (list, count, frame) => {
      const order = sortImpostors(list, count, frame.view);
      for (const part of parts) {
        part.material.setFloat('uHeight', frame.camera.viewport[1]);
        const mine = order.filter((i) => list[i * L.stride + L.sheet] === part.sheet && blendOf(puppet, list, i) === part.blend);
        part.mesh.write(list, mine, frame, part.size);
      }
    },
    setVisible: (on) => { for (const part of parts) part.mesh.mesh.isVisible = on; },
    meshes: () => parts.map((part) => part.mesh.mesh),
    dispose: () => {
      for (const part of parts) {
        part.mesh.dispose();
        part.material.dispose();
      }
      for (const t of textures) t.dispose();
    },
  };
}
